"use client";

import { useTransition } from "react";
import { useLocale } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { routing, type Locale } from "@/i18n/routing";

/**
 * Pill toggle FR/EN with sliding indicator.
 *
 * Smooth-switch contract :
 *   • `router.replace(pathname, { locale })` — preserves the page path,
 *     swaps only the locale segment
 *   • `useTransition` keeps the previous frame interactive while RSC streams
 *   • `scroll: false` — keeps scroll position across the swap
 *   • Prefetch on hover so the swap is sub-100ms on subsequent click
 *
 * Animation :
 *   • CSS-only translateX between equal-width slots. Was using Framer's
 *     `layoutId` for shared-element transitions, but when the LanguageSwitcher
 *     sits under a provider that rebuilds children on locale change (e.g.
 *     DashboardTour rebuilds its `steps` array → TourProvider re-renders the
 *     subtree), Framer loses the layoutId continuity and the pill either
 *     teleports without animating or disappears entirely. The pill is now
 *     always mounted; its `translateX` is derived from the active locale's
 *     index, so re-mounts are visually identical and never lose the indicator.
 */
export function LanguageSwitcher() {
  const current = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const switchTo = (next: Locale) => {
    if (next === current || isPending) return;
    startTransition(() => {
      router.replace(pathname, { locale: next, scroll: false });
    });
  };

  const activeIndex = Math.max(0, routing.locales.indexOf(current));

  return (
    <div
      className="relative inline-flex h-8 items-center rounded-full border border-border/60 bg-muted/40 p-0.5 text-xs font-medium"
      aria-busy={isPending}
    >
      {/* Sliding pill — always mounted. Width is half the inner track so it
          lines up exactly with each button; translateX(100%) slides it from
          the FR slot to the EN slot in one motion. */}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-0.5 left-0.5 top-0.5 w-[calc(50%-2px)] rounded-full bg-primary shadow-sm transition-transform duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none"
        style={{ transform: `translateX(${activeIndex * 100}%)` }}
      />
      {routing.locales.map((l) => {
        const isActive = l === current;
        return (
          <button
            key={l}
            type="button"
            onClick={() => switchTo(l)}
            onMouseEnter={() => {
              if (!isActive) router.prefetch(pathname, { locale: l });
            }}
            disabled={isPending}
            aria-current={isActive ? "true" : undefined}
            aria-label={l === "fr" ? "Français" : "English"}
            className={`relative z-10 flex h-full min-w-[2.25rem] flex-1 cursor-pointer items-center justify-center rounded-full px-2.5 transition-colors duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:cursor-not-allowed disabled:active:scale-100 ${
              isActive
                ? "text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            } ${isPending ? "opacity-60" : ""}`}
          >
            <span className="relative">{l.toUpperCase()}</span>
          </button>
        );
      })}
    </div>
  );
}
